// ⌘K quick-open: reach a loop, a workspace, or a page by typing from anywhere in the signed-in app.
// Mounted once in the shell. Ranking and grouping live in quickOpenResults.ts; this file owns fetching,
// keys, focus, and the listbox.
import { Activity, Network, Radio, Search, Settings, UsersRound } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { useAuth } from "./auth";
import { IconButton, ProviderMark } from "./components";
import type { LoopModel } from "./pages/LoopsPage";
import { RECENT_KEY, groupResults, loopItem, pageItems, readRecent, rememberRecent, shortcutLabel, workspaceItem, type QuickOpenItem } from "./quickOpenResults";

// Session caches: what the palette showed last time is shown at once and refreshed in the background.
const loopCache = new Map<string, LoopModel[]>();
const memberCountCache = new Map<string, number>();

const pageIcons: Record<string, typeof Activity> = { "/app": Activity, "/app/loops": Network, "/app/onboarding": Radio, "/app/people": UsersRound, "/app/settings": Settings };

function isShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k";
}

/** The visible control that opens the palette: a labelled bar in the sidebar, an icon in the mobile bar. */
export function QuickOpenTrigger({ variant = "bar", onOpen }: { variant?: "bar" | "icon"; onOpen: () => void }) {
  const keys = shortcutLabel(navigator.platform);
  const open = (event: ReactMouseEvent<HTMLButtonElement>) => {
    // Focus first so the palette hands focus back here on close, even where clicks do not focus buttons.
    event.currentTarget.focus();
    onOpen();
  };
  if (variant === "icon") return <IconButton className="icon-button--large quick-open-trigger--icon" label={`Search (${keys})`} onClick={open}><Search aria-hidden="true" /></IconButton>;
  return (
    <button type="button" className="quick-open-trigger" onClick={open} aria-keyshortcuts="Meta+K Control+K">
      <Search size={14} aria-hidden="true" />
      <span>Search</span>
      <kbd aria-hidden="true">{keys}</kbd>
    </button>
  );
}

export function QuickOpen({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { organizations, activeOrganization, setActiveOrganizationId } = useAuth();
  const navigate = useNavigate();
  const organizationId = activeOrganization?.id;
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [loops, setLoops] = useState<LoopModel[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // ⌘K / Ctrl+K toggles from anywhere in the signed-in app, including inside text fields. Esc closes
  // wherever focus sits, so a press that lands before the field takes focus still counts.
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (isShortcut(event)) {
        event.preventDefault();
        onOpenChange(!open);
      } else if (open && event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onOpenChange]);

  // Opening: remember where focus was, reset the query, focus the field. Closing hands focus back.
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQuery("");
    setActiveIndex(0);
    setRecent(readRecent(localStorage));
    setMemberCounts(Object.fromEntries(memberCountCache));
    inputRef.current?.focus();
    return () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (target?.isConnected) target.focus();
    };
  }, [open]);

  // Loops of the active workspace: cached for the session, shown at once, refreshed in the background.
  useEffect(() => {
    if (!open || !organizationId) return;
    const controller = new AbortController();
    const cached = loopCache.get(organizationId) ?? null;
    setLoops(cached);
    setFailed(false);
    setLoading(!cached);
    void api<{ loops: LoopModel[] }>("/api/app/loops", { signal: controller.signal }, organizationId)
      .then((data) => {
        loopCache.set(organizationId, data.loops);
        setLoops(data.loops);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        if (!cached) setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    if (activeOrganization?.kind === "group" && !memberCountCache.has(organizationId)) {
      void api<{ members: unknown[] }>("/api/app/group", { signal: controller.signal }, organizationId)
        .then((data) => {
          memberCountCache.set(organizationId, data.members.length);
          setMemberCounts(Object.fromEntries(memberCountCache));
        })
        .catch(() => undefined);
    }
    return () => controller.abort();
  }, [open, organizationId, activeOrganization?.kind]);

  const items = useMemo<QuickOpenItem[]>(() => [
    ...(loops ?? []).map(loopItem),
    ...organizations.map((organization) => workspaceItem(organization, memberCounts[organization.id])),
    ...pageItems(activeOrganization?.kind),
  ], [loops, organizations, memberCounts, activeOrganization?.kind]);
  const groups = useMemo(() => groupResults(items, query, recent), [items, query, recent]);
  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const activeItem = flat[Math.min(activeIndex, Math.max(0, flat.length - 1))];
  const activeId = activeItem ? `${listId}-${activeItem.id}` : undefined;

  useEffect(() => setActiveIndex(0), [query]);
  useEffect(() => {
    if (!open || !activeId) return;
    document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [open, activeId]);

  const pick = useCallback((item: QuickOpenItem) => {
    const next = rememberRecent(readRecent(localStorage), item.id);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage may be unavailable; the pick still works */ }
    if (item.kind === "workspace" && item.workspaceId) setActiveOrganizationId(item.workspaceId);
    else if (item.href) navigate(item.href);
    close();
  }, [close, navigate, setActiveOrganizationId]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab") {
      // The only tab stops are the field and, on small screens, the Cancel button.
      event.preventDefault();
      const cancelVisible = cancelRef.current && cancelRef.current.getClientRects().length > 0;
      if (cancelVisible && document.activeElement === inputRef.current) cancelRef.current?.focus();
      else inputRef.current?.focus();
      return;
    }
    if (document.activeElement !== inputRef.current) return;
    const count = flat.length;
    if (event.key === "ArrowDown" && count) { event.preventDefault(); setActiveIndex((index) => (Math.min(index, count - 1) + 1) % count); }
    else if (event.key === "ArrowUp" && count) { event.preventDefault(); setActiveIndex((index) => (Math.min(index, count - 1) - 1 + count) % count); }
    else if (event.key === "Home" && count) { event.preventDefault(); setActiveIndex(0); }
    else if (event.key === "End" && count) { event.preventDefault(); setActiveIndex(count - 1); }
    else if (event.key === "Enter" && activeItem) { event.preventDefault(); pick(activeItem); }
  };

  if (!open) return null;

  return (
    <div className="quick-open-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <div className="quick-open" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKeyDown}>
        <div className="quick-open__head">
          <Search size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label="Search loops, workspaces, and pages"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="go"
            placeholder="Search loops, workspaces, and pages"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button ref={cancelRef} type="button" className="quick-open__cancel" onClick={close}>Cancel</button>
        </div>
        <ul ref={listRef} id={listId} role="listbox" aria-label="Results" className="quick-open__list" onMouseDown={(event) => event.preventDefault()}>
          {groups.map((group) => (
            <li key={group.label} role="presentation" className="quick-open__group">
              <div id={`${listId}-${group.label}`} role="presentation" className="quick-open__label">{group.label}</div>
              <ul role="group" aria-labelledby={`${listId}-${group.label}`}>
                {group.items.map((item) => {
                  const rowIndex = flat.indexOf(item);
                  const isActive = item === activeItem;
                  const PageIcon = item.kind === "page" && item.href ? pageIcons[item.href] : undefined;
                  return (
                    <li
                      key={item.id}
                      id={`${listId}-${item.id}`}
                      role="option"
                      aria-selected={isActive}
                      className={`quick-open__row quick-open__row--${item.kind}${isActive ? " is-active" : ""}`}
                      onMouseMove={() => { if (rowIndex !== activeIndex) setActiveIndex(rowIndex); }}
                      onClick={() => pick(item)}
                    >
                      {item.kind === "loop" ? <ProviderMark provider={item.provider} size={24} className="quick-open__mark" />
                        : item.kind === "workspace" ? <span className="quick-open__mark quick-open__glyph" aria-hidden="true">{item.glyph}</span>
                        : <span className="quick-open__mark quick-open__glyph" aria-hidden="true">{PageIcon ? <PageIcon size={14} /> : null}</span>}
                      <span className="quick-open__name">{item.name}</span>
                      {item.fact ? <span className={`quick-open__fact${item.shared ? " is-shared" : ""}`}>{item.fact}</span> : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
        {flat.length === 0 && query.trim() && !loading ? <p className="quick-open__note" role="status">No match for “{query.trim()}”.</p> : null}
        {loading ? <p className="quick-open__note" role="status">Reading loops…</p> : failed ? <p className="quick-open__note" role="status">Loops could not be read. Workspaces and pages still open.</p> : null}
        <span className="sr-only" role="status" aria-live="polite">{query.trim() ? `${flat.length} ${flat.length === 1 ? "result" : "results"}` : ""}</span>
        <footer className="quick-open__foot" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </footer>
      </div>
    </div>
  );
}
